/**
 * Copyright 2016-2021 Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import SideBar from 'qwc2/components/SideBar';
import LocaleUtils from 'qwc2/utils/LocaleUtils';
import {
    highlightSelected,
    resetAdditionalIndicators,
    resetCompareIndicator,
    resetForIndicators,
    selectIndicator,
    setIndicators
} from "../actions/indicators";
import ConfigUtils from "qwc2/utils/ConfigUtils";
import clientAuth from "../ClientAuth";
import axios from "axios";
import TypeUtils from "../utils/TypeUtils";
import {addLayer, removeLayer} from "qwc2/actions/layers";
import IndicatorUtils from "../utils/IndicatorUtils";
import Icon from "qwc2/components/Icon";

class Indicators extends React.Component {

    static propTypes = {
        addLayer: PropTypes.func,
        currentTask: PropTypes.object,
        highlightSelected: PropTypes.func,
        indicators: PropTypes.array,
        removeLayer: PropTypes.func,
        resetAdditionalIndicators: PropTypes.func,
        resetCompareIndicator: PropTypes.func,
        resetForIndicators: PropTypes.func,
        selectIndicator: PropTypes.func,
        selectedDate: PropTypes.string,
        selectedIndicator: PropTypes.object,
        selectedObjectType: PropTypes.string,
        setIndicators: PropTypes.func,
        side: PropTypes.string,
        width: PropTypes.string
    }
    static defaultProps = {
        width: "25em",
        side: 'right'
    }
    state = {
        sidebarwidth: null,
        topics: [],
        open: [],
    }

    getUrl() {
        return ConfigUtils.getConfigProp("indicatorsServiceUrl");
    }

    componentDidMount() {
        this.loadData();
    }

    extractUniqueIndicators(topics) {
        const seen = new Map();

        function visit(node) {
            for (const indicator of node.indicators ?? []) {
                if (!seen.has(indicator.id)) {
                    seen.set(indicator.id, indicator);
                }
            }

            for (const child of node.children ?? []) {
                visit(child);
            }
        }

        for (const topic of topics ?? []) {
            visit(topic);
        }

        return [...seen.values()];
    }

    loadData() {
        axios.get(this.getUrl() + 'indicator/hierarchy/', clientAuth.headers())
            .catch(() => null)
            .then(res => {
                if (res && res.data && res.data.topics) {
                    const valid = this.extractUniqueIndicators(res.data.topics);
                    this.props.setIndicators(valid);
                    this.setState({topics: res.data.topics});
                    this.openAll();
                } else {
                    this.props.setIndicators([]);
                    this.setState({topics: []});
                }
            });
    }

    openAll() {
        if (!this.props.indicators || !this.state.topics) {
            return;
        }
        if (this.props.indicators.length > 0 && this.state.topics.length > 0) {
            this.state.topics.forEach(topic => this.toggleTopic(topic.id));
        }
    }

    selectIndicator(indicator, objectType) {
        if (this.props.selectedIndicator) {
            const oldLayer = IndicatorUtils.getLayer(this.props.selectedIndicator, this.props.selectedObjectType, this.props.selectedDate);
            this.props.removeLayer(oldLayer.id);
            if (objectType !== this.props.selectedObjectType) {
                this.props.resetForIndicators();
                this.props.highlightSelected();
            }
            this.props.resetAdditionalIndicators();
            this.props.resetCompareIndicator();
        }

        if (this.isActive(indicator, objectType)) {
            this.props.selectIndicator(null, null, null);
            return;
        }

        const layer = IndicatorUtils.getLayer(indicator, objectType);

        this.props.addLayer(layer);
        console.log('Select i', indicator, objectType);
        this.props.selectIndicator(indicator, objectType, IndicatorUtils.getDefaultDate(indicator));
    }

    getLabel(type) {
        return TypeUtils.getTypeLabel(type);
    }

    isActive(indicator, objectType) {
        if (!this.props.selectedIndicator) {
            return false;
        }
        if (indicator.id !== this.props.selectedIndicator.id) {
            return false;
        }
        if (objectType !== this.props.selectedObjectType) {
            return false;
        }
        return true;
    }

    getCssClass(indicator, objectType) {
        return this.isActive(indicator, objectType) ? 'active' : null;
    }

    listIndicators() {

        return (
            <div className="indicators">
                {this.renderIndicatorTopics(this.state.topics, 1)}
            </div>
        );
    }

    toggleTopic(topicId) {
        const open = this.state.open;
        if (open.indexOf(topicId) > -1) {
            open.splice(open.indexOf(topicId, 0), 1);
        } else {
            open.push(topicId);
        }
        this.setState({open: open});
    }

    renderIndicatorTopics(topics, level) {
        if (!topics) {
            return null;
        }
        if (topics.length == 0) {
            return null;
        }
        return (
            <ul>
                {topics.map(topic => {
                    let style = {display: 'none'};
                    let icon = "tree_plus";
                    if (this.state.open.indexOf(topic.id) > -1) {
                        style = {};
                        icon = "tree_minus";
                    }

                    const subTopics = this.renderIndicatorTopics(topic.children, level + 1);
                    const indicators = this.renderIndicators(topic.indicators);

                    let toggle = null;
                    toggle = (
                        <Icon className="indicator-topics-expander" icon={icon}
                              onClick={() => this.toggleTopic(topic.id)}/>
                    );

                    return (
                        <li className={"indicatortopic indicatortopiclevel" + level} key={topic.id}>
                            <div className="indicatortogglename">
                                {toggle}
                                <span>{topic.name}</span>
                            </div>
                            <div style={style}>
                                {subTopics}
                                {indicators}
                            </div>
                        </li>
                    );
                })}
            </ul>
        );
    }

    renderIndicators(indicators) {
        if (!indicators || indicators.length == 0) {
            return null;
        }
        return (
            <ul>
                {indicators.map(indicator => {
                    if (!indicator.indicatorValueObjectTypes || indicator.indicatorValueObjectTypes.length == 0) {
                        return null;
                    }
                    if (!indicator.timeSlices || indicator.timeSlices.length == 0) {
                        return null;
                    }
                    return (
                        <li className="indicator" key={indicator.id}>
                            <span>{indicator.name}</span>
                            <div>
                                {indicator.indicatorValueObjectTypes.map(ot => {
                                    return (
                                        <span className={"indicatorobject " + this.getCssClass(indicator, ot)}
                                              key={indicator.id + "." + ot}
                                              onClick={() => this.selectIndicator(indicator, ot)}>
                                        {this.getLabel(ot)}
                                    </span>
                                    );
                                })}
                            </div>
                        </li>
                    );
                })}
            </ul>
        );
    }

    render() {
        let indicatorList = (
            <div>Es sind keine Indikatoren vorhanden.</div>
        );
        if (this.props.indicators.length > 0 && this.state.topics.length > 0) {
            indicatorList = this.listIndicators();
        }

        return (
            <div>
                <SideBar icon="arrows-to-eye" id="Indicators"
                         side={this.props.side}
                         title={LocaleUtils.tr("appmenu.items.Indicators")} width={this.state.sidebarwidth || this.props.width}>
                    {() => ({
                        body: (
                            <div>
                                {indicatorList}
                            </div>
                        )
                    })}
                </SideBar>
            </div>
        );
    }
}

const selector = (state) => ({
    indicators: state.indicators.indicators,
    selectedIndicator: state.indicators.selectedIndicator,
    selectedObjectType: state.indicators.selectedObjectType,
    selectedDate: state.indicators.selectedDate
});

export default connect(selector, {
    setIndicators: setIndicators,
    selectIndicator: selectIndicator,
    addLayer: addLayer,
    removeLayer: removeLayer,
    highlightSelected: highlightSelected,
    resetForIndicators: resetForIndicators,
    resetAdditionalIndicators: resetAdditionalIndicators,
    resetCompareIndicator: resetCompareIndicator,
})(Indicators);
